import type {ReactNode} from 'react';
import {Navigate, Outlet} from 'react-router-dom';
import {usePermissions} from '@common-web/hooks/use-permissions';

/**
 * Route-level permission guard. Renders children only when the current user
 * holds the given permission key (or, when an array is passed, ANY of the
 * keys); otherwise redirects. Must be mounted inside `AuthGuard` so the
 * session (and its permission set) is already resolved.
 *
 * Without children it renders an `<Outlet />`, so it can also guard a group
 * of routes as a pathless layout route.
 *
 * `redirectTo` must point at a route that is NOT gated on the same key —
 * there is deliberately no loop protection here, so a self-referential
 * redirect target would navigate forever. Use an ungated landing route
 * (e.g. `/groups`).
 */
export function RequirePermission({
  permission,
  redirectTo,
  children,
}: {
  permission: string | readonly string[];
  redirectTo: string;
  children?: ReactNode;
}) {
  const permissions = usePermissions();
  const keys = typeof permission === 'string' ? [permission] : permission;
  const allowed = keys.some((key) => permissions.has(key));
  if (!allowed) return <Navigate to={redirectTo} replace />;
  return children !== undefined ? <>{children}</> : <Outlet />;
}
